import { useCallback, useEffect } from 'react';
import * as boardsStore from '../../extensions/bundled/task-manager/boardsStore';
import { restoreBoardChanges, takeBoardChanges } from '../../extensions/bundled/task-manager/boardChangeLog';

// Team boards only: ceiling on how long a continuous edit burst (e.g. typing a
// task description non-stop) can keep re-arming the 400ms save debounce
// before scheduleDebounce forces a persist anyway — bounds realtime staleness
// for teammates viewing the same board without saving on every keystroke. The
// engine's own TEAM_DEBOUNCE_MS (src-tauri/src/sync/engine.rs) adds ~250ms
// more before the push actually goes out.
const TEAM_SAVE_MAX_WAIT_MS = 1500;

/**
 * Writing the open boards to disk: the per-board dirty queue, the 400 ms
 * debounce (with a ceiling on team boards), retries, and the explicit flushes.
 * Moved out of TaskBoardTab unchanged; every ref is still owned by the tab.
 */
export default function useBoardPersistence({
  aliveRef,
  boardRef,
  burstStartRef,
  deletedIdsRef,
  dirtyEntityIdsRef,
  dirtyRef,
  persistRef,
  pullPendingRef,
  recomputeSaveState,
  retryTimeoutRef,
  saveTimeoutRef,
  savingRef,
}) {
  // Retry any writes that failed, on a short timer, until they land. One timer
  // is kept (retryTimeoutRef); it reschedules itself while anything is still
  // dirty. persistRef breaks the persist<->scheduleRetry reference cycle.
  const scheduleRetry = useCallback(() => {
    if (retryTimeoutRef.current || !aliveRef.current) return;
    retryTimeoutRef.current = setTimeout(() => {
      retryTimeoutRef.current = null;
      persistRef.current?.();
    }, 2000);
  }, [aliveRef, persistRef, retryTimeoutRef]);

  // Write every dirty board to disk. Per-board and idempotent: savingRef guards
  // overlapping writes to the same file, and a dirty entry is cleared only if no
  // newer edit superseded that exact version while the write was in flight
  // (otherwise it stays queued for the next persist). Anything still dirty
  // afterwards (a failed write) is retried.
  const persist = useCallback(async () => {
    if (pullPendingRef.current) return; // reconcileFromDisk rebases, then flushes
    const entries = [...dirtyRef.current.entries()];
    await Promise.all(
      entries.map(async ([id, snapshot]) => {
        if (savingRef.current.has(id)) return;
        if (deletedIdsRef.current.has(id)) { dirtyRef.current.delete(id); dirtyEntityIdsRef.current.delete(id); return; }
        savingRef.current.add(id);
        // Only what THIS person changed, field by field (boardChangeLog.js): a
        // whole-board write re-sent teammates' cards from this screen's copy
        // and tombstoned the ones it had not seen yet.
        const changes = takeBoardChanges(id);
        try {
          try {
            await boardsStore.writeBoard(snapshot, { changes });
          } catch (err) {
            restoreBoardChanges(id, changes);
            throw err;
          }
          // Deleted while our write was in flight: our write just resurrected the
          // file — remove it again so the delete wins.
          if (deletedIdsRef.current.has(id)) {
            dirtyRef.current.delete(id);
            dirtyEntityIdsRef.current.delete(id);
            await boardsStore.deleteBoard(id, boardsStore.boardScopeKey(snapshot)).catch(() => {});
          } else if (dirtyRef.current.get(id) === snapshot) {
            // This exact snapshot made it to disk with nothing newer queued
            // behind it — the entities it covered are no longer at risk from
            // a live WS update, so the realtime fast path can touch them again.
            dirtyRef.current.delete(id);
            dirtyEntityIdsRef.current.delete(id);
          }
        } catch {
          /* keep in dirtyRef for retry */
        } finally {
          savingRef.current.delete(id);
        }
      })
    );
    recomputeSaveState();
    if (dirtyRef.current.size) scheduleRetry();
  }, [pullPendingRef, dirtyRef, recomputeSaveState, scheduleRetry, savingRef, deletedIdsRef, dirtyEntityIdsRef]);
  persistRef.current = persist;

  // Debounced persistence, scheduled imperatively from updateBoard (real user
  // edits) so programmatic setBoard (loads, sync re-reads) never writes back.
  const scheduleDebounce = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    const isTeam = boardRef.current?.visibility === 'team';
    if (!isTeam) {
      burstStartRef.current = null;
    } else {
      if (burstStartRef.current == null) burstStartRef.current = Date.now();
      if (Date.now() - burstStartRef.current >= TEAM_SAVE_MAX_WAIT_MS) {
        // Ceiling already reached this burst: flush now instead of arming
        // another 400ms window that would just push the wait out further.
        burstStartRef.current = null;
        saveTimeoutRef.current = null;
        persistRef.current?.();
        return;
      }
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      burstStartRef.current = null;
      persistRef.current?.();
    }, 400);
  }, [boardRef, burstStartRef, persistRef, saveTimeoutRef]);

  // Persist now: cancel the pending debounce and flush all dirty boards. Used
  // when leaving a board, on unmount, and before a cross-device re-read.
  const flushSave = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    burstStartRef.current = null;
    await persist();
  }, [burstStartRef, persist, saveTimeoutRef]);

  // Team boards only: skip the 400ms client debounce for edits that are
  // already "settled" (a whole undo-able action — add/delete/rename/drop —
  // not a keystroke), so they hit the wire as fast as chat's WS-first send
  // instead of trailing by up to 400ms client debounce + the engine's own
  // 250ms TEAM_DEBOUNCE_MS coalescing before the push even starts. Continuous
  // streams (drag-over frames, description typing) stay on the debounce —
  // see call sites — so a fast drag or fast typing doesn't flood the network.
  const flushIfTeamBoard = useCallback(() => {
    if (boardRef.current?.visibility === 'team') flushSave();
  }, [boardRef, flushSave]);

  // Flush on unmount, and clear the retry timer so it can't fire after unmount.
  // Without the flush, closing the tab inside the 400ms debounce window would
  // drop the last edit.
  useEffect(() => {
    aliveRef.current = true; // (re)mount — also resets after a StrictMode remount
    return () => {
      aliveRef.current = false; // block any post-unmount retry rescheduling
      pullPendingRef.current = false; // last chance to write; nothing reconciles after this
      flushSave();
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [aliveRef, flushSave, pullPendingRef, retryTimeoutRef]);

  return { scheduleDebounce, flushSave, flushIfTeamBoard };
}
